<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { NButton, NModal } from 'naive-ui'
import { lighten, rgba } from 'polished'
import { LottieAnimation, SvgIcon } from '@/components/common'
import { useInsufficientCreditModal } from '@/composables/useInsufficientCreditModal'
import { useUpgradeToProModal } from '@/composables/useUpgradeToProModal'
import { useAuthStore } from '@/store'
import { useTheme } from '@/hooks/useTheme'
import { t } from '@/locales'
import { globalEventEmitter } from '@/utils/eventEmitter'
import addCredit from '@/assets/lottie-files/billing/addCredit.json'
import upToPro from '@/assets/lottie-files/billing/upToPro.json'
import sessionExpired from '@/assets/lottie-files/expried/session_expired.json'

// const { basicCreditBalance, advancedCreditBalance } = useCredit()
const { showModal: showInsufficientModal, handleRecharge } = useInsufficientCreditModal()
const { showModal: showUpgradeModal, handleUpgrade } = useUpgradeToProModal()
const { logout } = useAuthStore()
const { themeOverrides, isDark } = useTheme()

// Session expired modal
const showSessionExpiredModal = ref(false)

// Computed property for primary color
const primaryColor = computed(() => themeOverrides.value.common?.primaryColor || '#3b82f6')
const primaryColorLight = computed(() => lighten(0.05, primaryColor.value)) // Tăng từ 0.01 lên 0.05
const primaryColorRgba = computed(() => rgba(primaryColor.value, 0.05)) // Giảm alpha

// Session expired modal background
const sessionExpiredModalStyle = computed(() => ({
  width: '560px',
  maxWidth: '95vw',
  borderRadius: '24px',
  background: isDark.value
    ? 'linear-gradient(135deg, #7f1d1d 0%, #9a3412 50%, #854d0e 100%)'
    : 'linear-gradient(135deg, #fef2f2 0%, #fed7aa 50%, #fef3c7 100%)',
  border: isDark.value
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(0, 0, 0, 0.1)',
}))

const gradientStyle = computed(() => {
  return {
    background: `linear-gradient(to bottom right, ${primaryColorRgba.value}, ${rgba(primaryColorLight.value, 0.5)})`,
  }
})

// Computed property for v-model - Insufficient Credit Modal
const insufficientModalShow = computed({
  get: () => showInsufficientModal.value,
  set: (value: boolean) => {
    showInsufficientModal.value = value
  },
})

// Computed property for v-model - Upgrade to Pro Modal
const upgradeModalShow = computed({
  get: () => showUpgradeModal.value,
  set: (value: boolean) => {
    showUpgradeModal.value = value
  },
})

// Computed property for v-model - Session Expired Modal
const sessionExpiredModalShow = computed({
  get: () => showSessionExpiredModal.value,
  set: (value: boolean) => {
    showSessionExpiredModal.value = value
  },
})

const _handleCloseInsufficient = () => {
  showInsufficientModal.value = false
}

const _handleCloseUpgrade = () => {
  showUpgradeModal.value = false
}

const _handleCloseSessionExpired = () => {
  showSessionExpiredModal.value = false
}

const handleLoginAgain = () => {
  showSessionExpiredModal.value = false
  logout()
}

// Listen for show modal events
const handleShowInsufficientModal = () => {
  showInsufficientModal.value = true
}

const handleShowUpgradeModal = () => {
  showUpgradeModal.value = true
}

const handleShowSessionExpiredModal = () => {
  showSessionExpiredModal.value = true
}

onMounted(() => {
  globalEventEmitter.on('show-insufficient-credit-modal', handleShowInsufficientModal)
  globalEventEmitter.on('show-upgrade-to-pro-modal', handleShowUpgradeModal)
  globalEventEmitter.on('show-session-expired-modal', handleShowSessionExpiredModal)
})

onUnmounted(() => {
  globalEventEmitter.off('show-insufficient-credit-modal', handleShowInsufficientModal)
  globalEventEmitter.off('show-upgrade-to-pro-modal', handleShowUpgradeModal)
  globalEventEmitter.off('show-session-expired-modal', handleShowSessionExpiredModal)
})
</script>

<template>
  <!-- Insufficient Credit Modal (Error 402) -->
  <NModal v-model:show="insufficientModalShow" preset="card" style="width: 600px; max-width: 90vw;">
    <div class="text-center">
      <!-- Header with icon and close button -->
      <div
        class="w-48 h-48 rounded-full flex items-center justify-center mx-auto"
      >
        <LottieAnimation
          :animation-data="addCredit"
          :loop="true"
          :autoplay="true"
          :speed="1"
          :width="400"
          :height="400"
        />
      </div>
      <div class="flex justify-center items-center mb-6">
        <div class="flex items-center gap-3">
          <div class="text-center">
            <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">
              {{ t('modals.insufficientCredit.title') }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {{ t('modals.insufficientCredit.subtitle') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Feature list with gradient background -->
      <div
        class="rounded-2xl p-6 mb-6 text-left"
        :style="gradientStyle"
      >
        <div class="space-y-4">
          <!-- Feature 1 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:document-text"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
              {{ t('modals.insufficientCredit.features.createExam') }}
            </p>
          </div>

          <!-- Feature 2 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:sparkles"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
              {{ t('modals.insufficientCredit.features.aiAssistant') }}
            </p>
          </div>

          <!-- Feature 3 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:search"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
              {{ t('modals.insufficientCredit.features.analyzeContent') }}
            </p>
          </div>

          <!-- Feature 4 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:image"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ t('modals.insufficientCredit.features.generateImage') }}
            </p>
          </div>

          <!-- Feature 5 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:bulb"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ t('modals.insufficientCredit.features.mindMap') }}
            </p>
          </div>

          <!-- Feature 6 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:school"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ t('modals.insufficientCredit.features.learningTools') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Call to action button -->
      <NButton
        type="primary"
        size="large"
        class="w-full h-12 text-base font-medium rounded-2xl"
        :style="{ backgroundColor: primaryColor, width: '100%' }"
        @click="handleRecharge"
      >
        <template #icon>
          <SvgIcon icon="ion:card" class="text-lg" />
        </template>
        {{ t('modals.insufficientCredit.button') }}
      </NButton>
    </div>
  </NModal>

  <!-- Upgrade to Pro Modal (Error 512) -->
  <NModal v-model:show="upgradeModalShow" preset="card" style="width: 600px; max-width: 90vw;">
    <div class="text-center">
      <!-- Header with icon and close button -->
      <div
        class="w-48 h-48 rounded-full flex items-center justify-center mx-auto"
      >
        <LottieAnimation
          :animation-data="upToPro"
          :loop="true"
          :autoplay="true"
          :speed="1"
        />
      </div>
      <div class="flex justify-center items-center mb-6">
        <div class="flex items-center gap-3">
          <div class="text-center">
            <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">
              {{ t('modals.upgradeToPro.title') }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {{ t('modals.upgradeToPro.subtitle') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Feature list with gradient background -->
      <div
        class="rounded-2xl p-6 mb-6 text-left"
        :style="gradientStyle"
      >
        <div class="space-y-4">
          <!-- Feature 1 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:card"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
              {{ t('modals.upgradeToPro.features.abundantCredits') }}
            </p>
          </div>

          <!-- Feature 2 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:sparkles"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
              {{ t('modals.upgradeToPro.features.aiModels') }}
            </p>
          </div>

          <!-- Feature 3 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:image"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
              {{ t('modals.upgradeToPro.features.imageGeneration') }}
            </p>
          </div>

          <!-- Feature 4 -->
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <SvgIcon
                icon="ion:school"
                class="text-sm"
                :style="{ color: isDark ? 'white' : primaryColor }"
              />
            </div>
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ t('modals.upgradeToPro.features.learningTools') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Call to action button -->
      <NButton
        type="primary"
        size="large"
        class="w-full h-12 text-base font-medium rounded-2xl"
        :style="{ backgroundColor: primaryColor, width: '100%' }"
        @click="handleUpgrade"
      >
        <template #icon>
          <SvgIcon icon="ion:rocket" class="text-lg" />
        </template>
        {{ t('modals.upgradeToPro.button') }}
      </NButton>
    </div>
  </NModal>

  <!-- Session Expired Modal (Error 401) -->
  <NModal
    v-model:show="sessionExpiredModalShow"
    :closable="false"
    :mask-closable="false"
    :close-on-esc="false"
    :close-on-overlay-click="false"
    preset="card"
    class="session-expired-modal"
    :style="sessionExpiredModalStyle"
  >
    <div class="text-center relative overflow-hidden">
      <!-- Animated background elements -->
      <div class="absolute top-4 right-6 w-16 h-16 bg-red-100/60 dark:bg-red-800/30 rounded-full animate-pulse" />
      <div class="absolute bottom-8 left-4 w-12 h-12 bg-orange-100/60 dark:bg-orange-800/30 rounded-full animate-bounce" style="animation-delay: 0.5s;" />

      <!-- Content -->
      <div class="relative z-10">
        <!-- Header with Lottie animation -->
        <div class="w-56 h-56 flex items-center justify-center mx-auto">
          <LottieAnimation
            :animation-data="sessionExpired"
            :loop="true"
            :autoplay="true"
            :speed="1.2"
            :width="240"
            :height="240"
            class="drop-shadow-lg"
          />
        </div>

        <!-- Title and subtitle -->
        <div class="mb-8">
          <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3 leading-tight">
            {{ t('modals.sessionExpired.title') }}
          </h3>
          <p class="text-gray-600 dark:text-gray-400 text-base leading-relaxed max-w-md mx-auto">
            {{ t('modals.sessionExpired.subtitle') }}
          </p>
        </div>

        <!-- Security notice -->
        <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-2xl p-4 mb-6 text-left">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <SvgIcon
                icon="ion:shield-checkmark"
                class="text-lg text-amber-600 dark:text-amber-400"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
                {{ t('modals.sessionExpired.securityTitle') }}
              </p>
              <p class="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                {{ t('modals.sessionExpired.securityDescription') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Action button with animation -->
        <NButton
          type="error"
          size="large"
          class="w-full h-14 text-base font-semibold rounded-2xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg session-expired-button"
          @click="handleLoginAgain"
        >
          <template #icon>
            <SvgIcon icon="ion:log-in-outline" class="text-xl" />
          </template>
          {{ t('modals.sessionExpired.button') }}
        </NButton>

        <!-- Additional help text -->
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-4 opacity-75">
          <SvgIcon icon="ion:information-circle" class="inline mr-1" />
          {{ t('modals.sessionExpired.helpText') }}
        </p>
      </div>
    </div>
  </NModal>
</template>

<style scoped>
.modal-card {
  background:
    radial-gradient(circle at 20% 20%, v-bind('primaryColorRgba') 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, v-bind('rgba(primaryColorLight, 0.1)') 0%, transparent 50%);
}

/* Session expired modal styling */
.session-expired-modal :deep(.n-card) {
  border: none;
  box-shadow: 0 25px 50px -12px rgba(239, 68, 68, 0.25);
}

.session-expired-button {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3) !important;
  position: relative;
  overflow: hidden;
}

.session-expired-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease-in-out;
}

.session-expired-button:hover::before {
  left: 100%;
}

.session-expired-button:hover {
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4) !important;
}

/* Enhanced animations */
@keyframes sessionExpiredPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

.session-expired-modal .animate-pulse {
  animation: sessionExpiredPulse 2s ease-in-out infinite;
}

@keyframes sessionExpiredBounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -8px, 0);
  }
  70% {
    transform: translate3d(0, -4px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
}

.session-expired-modal .animate-bounce {
  animation: sessionExpiredBounce 2s ease-in-out infinite;
}
</style>
